import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, Loader2, Check, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function Feedback() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setLoading(true);
    try {
      await api.post("/feedback", { text: text.trim(), name });
      setSent(true);
      toast.success("Thank you — your feedback was received.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="px-6 py-8 md:max-w-2xl md:mx-auto md:py-14 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <button
        data-testid="feedback-back"
        onClick={() => navigate("/settings")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#00E5FF] mb-6 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Settings
      </button>

      <h1 className="text-3xl text-white mb-2">Share your thoughts</h1>
      <p className="text-sm text-slate-400 mb-8 leading-relaxed">
        Feedback, feature ideas, or anything that would make your study richer. Dwight reads every one.
      </p>

      {sent ? (
        <div data-testid="feedback-success" className="rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-6 text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-[#00E5FF] flex items-center justify-center mx-auto">
            <Check className="w-6 h-6 text-white" strokeWidth={2} />
          </div>
          <p className="text-slate-300">Thank you. Your feedback was received.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 pt-1">
            <Button
              data-testid="feedback-back-to-study"
              onClick={() => navigate("/study")}
              className="rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] gap-1.5"
            >
              <BookOpen className="w-4 h-4" strokeWidth={1.75} /> Back to Study
            </Button>
            <Button onClick={() => { setSent(false); setText(""); }} variant="outline" className="rounded-full border-[#29344D]">
              Send more
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Input
            data-testid="feedback-name"
            placeholder="Your name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-12 rounded-xl border-[#29344D] bg-[#0E131F]"
          />
          <Textarea
            data-testid="feedback-text"
            placeholder="What's on your heart?"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            required
            className="rounded-xl border-[#29344D] bg-[#0E131F] resize-none focus-visible:ring-[#00E5FF]/30"
          />
          <Button
            data-testid="feedback-submit"
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E]"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit feedback"}
          </Button>
        </form>
      )}
    </div>
  );
}
